<template>
  <div class="remarks-page">
    <header class="remarks-header">
      <div>
        <p class="eyebrow">Administration</p>
        <h1>Remarques employés</h1>
        <p>Suivi mensuel des remarques RH par employé et par poste.</p>
      </div>
    </header>

    <section class="control-panel">
      <div class="search-block">
        <label>Rechercher un employé</label>
        <div class="search-input-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input v-model="searchQuery" type="search" placeholder="Nom, prénom, poste..." @input="searchUsers" />
        </div>
        <div v-if="users.length" class="search-menu">
          <button v-for="staff in users" :key="staff.Id" type="button" @click="fetchRemarques(staff)">
            <img :src="staff.Pic || '/pointage/profile/profile.jpg'" :alt="staff.First_name" />
            <span>
              <strong>{{ staff.First_name }} {{ staff.Last_name }}</strong>
              <small>{{ staff.Post_name }}</small>
            </span>
          </button>
        </div>
        <div v-if="searchError" class="inline-error">{{ searchError }}</div>
      </div>

      <div class="filters-block">
        <label>
          Mois
          <MonthPickerButton v-model="selectedMonth" @change="onMonthChange" />
        </label>
        <label>
          Poste
          <select v-model="selectedPost" @change="fetchRemarquesByMass">
            <option value="all">Tous les postes</option>
            <option v-for="post in posts" :key="post.Post_name" :value="post.Post_name">
              {{ post.Post_name }}
            </option>
          </select>
        </label>
      </div>
    </section>

    <section v-if="selectedStaff" class="staff-workspace">
      <aside class="staff-card">
        <button class="btn-ghost back-btn" type="button" @click="backToMass">Retour liste</button>
        <img :src="staffAvatar" :alt="staffName" />
        <h2>{{ staffName }}</h2>
        <span>{{ staffPost }}</span>
        <strong>{{ records.length }} remarque{{ records.length > 1 ? 's' : '' }}</strong>
      </aside>

      <main class="remarks-workspace">
        <section class="note-card">
          <div class="section-head">
            <div>
              <p class="eyebrow">Nouvelle remarque</p>
              <h3>Ajouter au dossier</h3>
            </div>
          </div>
          <textarea v-model="newRemark" placeholder="Écrire une remarque claire et utile pour le suivi RH..."></textarea>
          <div class="note-actions">
            <span>{{ newRemark.trim().length }} caractère{{ newRemark.trim().length > 1 ? 's' : '' }}</span>
            <button class="btn-primary" type="button" :disabled="savingRemark || !newRemark.trim()" @click="addRemarque">
              {{ savingRemark ? 'Ajout...' : 'Ajouter la remarque' }}
            </button>
          </div>
        </section>

        <section class="list-card">
          <div class="section-head">
            <div>
              <p class="eyebrow">Historique</p>
              <h3>Remarques du mois</h3>
            </div>
            <span class="count-pill">{{ records.length }}</span>
          </div>

          <div v-if="loading" class="empty-state">Chargement...</div>
          <div v-else-if="fetchError && !records.length" class="empty-state">{{ fetchError }}</div>
          <div v-else-if="!records.length" class="empty-state">Aucune remarque pour ce mois.</div>
          <div v-else class="remark-list">
            <article v-for="record in records" :key="record.id" class="remark-row">
              <div class="remark-body">
                <p>{{ record.remarque }}</p>
                <span>{{ convertdate(record.created_at) }}</span>
              </div>
              <div class="row-actions">
                <button type="button" class="icon-btn" title="Modifier" @click="openEditRemarque(record)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                  </svg>
                </button>
                <button type="button" class="icon-btn danger" title="Supprimer" @click="deleteRemarque(record)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="m19 6-1 14H6L5 6"/><path d="M10 11v5M14 11v5"/>
                  </svg>
                </button>
              </div>
            </article>
          </div>
        </section>
      </main>
    </section>

    <section v-else class="mass-workspace">
      <div v-if="totalRemarks > 0" class="summary-grid">
        <div class="metric-card">
          <span>Employés concernés</span>
          <strong>{{ totalStaffWithRemarks }}</strong>
        </div>
        <div class="metric-card">
          <span>Total remarques</span>
          <strong>{{ totalRemarks }}</strong>
        </div>
        <div class="metric-card">
          <span>Postes affichés</span>
          <strong>{{ Object.keys(staffRemarksMass).length }}</strong>
        </div>
      </div>

      <div v-if="loading" class="empty-state">Chargement...</div>
      <div v-else-if="fetchMassError" class="empty-state">{{ fetchMassError }}</div>
      <div v-else class="post-groups">
        <section v-for="(staffList, postName) in staffRemarksMass" :key="postName" class="post-group">
          <div class="post-group-head">
            <h2>{{ postName }}</h2>
            <span>{{ staffList.length }} employé{{ staffList.length > 1 ? 's' : '' }}</span>
          </div>
          <button v-for="staff in staffList" :key="staff.id" class="staff-row" type="button" @click="openStaffFromMass(staff, postName)">
            <span>
              <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
              <small>{{ postName }}</small>
            </span>
            <em>{{ staff.remark_count }}</em>
          </button>
        </section>
      </div>
    </section>

    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Modifier la remarque</h3>
          <button type="button" class="icon-btn" @click="closeEditModal">×</button>
        </div>
        <textarea v-model="editText" class="modal-textarea"></textarea>
        <div class="modal-footer">
          <button type="button" class="btn-ghost" @click="closeEditModal">Annuler</button>
          <button type="button" class="btn-primary" @click="updateRemarque">Enregistrer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  inject: ['privilage'],
  name: 'AppStaffRemarques',
  components: { MonthPickerButton },
  props: {
    staffId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      fetchMassError: null,
      selectedMonth: '',
      posts: [],
      selectedPost: 'all',
      staffRemarksMass: {},
      fetchError: null,
      searchError: null,
      selectedStaff: null,
      searchQuery: '',
      users: [],
      records: [],
      loading: false,
      reportTitle: '',
      editingRemarque: null,
      editText: '',
      showEditModal: false,
      newRemark: '',
      savingRemark: false,
    };
  },
  computed: {
    staffName() {
      if (!this.selectedStaff) return '';
      return `${this.selectedStaff.First_name || this.selectedStaff.first_name || ''} ${this.selectedStaff.Last_name || this.selectedStaff.last_name || ''}`.trim();
    },
    staffPost() {
      return this.selectedStaff?.Post_name || this.selectedStaff?.post_name || '';
    },
    staffAvatar() {
      return this.selectedStaff?.Pic || '/pointage/profile/profile.jpg';
    },
    totalStaffWithRemarks() {
      return Object.values(this.staffRemarksMass).reduce((sum, list) => sum + list.length, 0);
    },
    totalRemarks() {
      return Object.values(this.staffRemarksMass).flat().reduce((sum, staff) => sum + Number(staff.remark_count || 0), 0);
    },
  },
  watch: {
    staffId: {
      immediate: true,
      handler() {
        if (this.selectedStaff) this.fetchRemarques(this.selectedStaff);
      },
    },
    searchQuery(value) {
      if (!value) {
        this.users = [];
        this.searchError = null;
      }
    },
  },
  mounted() {
    const date = new Date();
    this.selectedMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    this.fetchPosts();
    this.fetchRemarquesByMass();
  },
  methods: {
    onMonthChange() {
      if (this.selectedStaff) this.fetchRemarques(this.selectedStaff);
      else this.fetchRemarquesByMass();
    },
    backToMass() {
      this.selectedStaff = null;
      this.records = [];
      this.fetchError = null;
      this.newRemark = '';
      this.fetchRemarquesByMass();
    },
    openStaffFromMass(staff, postName) {
      this.fetchRemarques({
        Id: staff.id,
        First_name: staff.first_name,
        Last_name: staff.last_name,
        Post_name: postName,
        Pic: null,
      });
    },
    async searchUsers() {
      const query = this.searchQuery.trim();
      this.records = [];
      if (query.length < 2) {
        this.users = [];
        this.searchError = null;
        return;
      }
      try {
        const res = await fetch(`/api-pointage/search?query=${encodeURIComponent(query)}`, { cache: 'no-store' });
        if (!res.ok) throw new Error("Pas d'employé trouvé");
        const data = await res.json();
        this.users = Array.isArray(data) ? data : [];
        this.searchError = this.users.length ? null : 'Aucun employé trouvé';
      } catch (err) {
        this.users = [];
        this.searchError = err.message;
      }
    },
    async fetchRemarques(staff) {
      const id = staff?.Id || staff?.id;
      if (!id) return;
      this.selectedStaff = staff;
      this.loading = true;
      this.fetchError = null;
      this.users = [];
      try {
        const res = await fetch(`/api-pointage/getremarques?staff_id=${id}&month=${this.selectedMonth}`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Erreur chargement remarques');
        const data = await res.json();
        this.records = Array.isArray(data) ? data : [];
        if (!this.records.length) this.fetchError = "Cet employé n'a aucune remarque pour ce mois.";
      } catch (err) {
        this.records = [];
        this.fetchError = err.message;
      } finally {
        this.loading = false;
      }
    },
    async fetchRemarquesByMass() {
      this.loading = true;
      this.fetchMassError = null;
      try {
        let url = `/api-pointage/getremarquesbymass?month=${this.selectedMonth}`;
        if (this.selectedPost && this.selectedPost !== 'all') {
          url += `&post=${encodeURIComponent(this.selectedPost)}`;
        }
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error('Erreur chargement remarques');
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        const groups = {};
        rows.forEach((staff) => {
          if (!groups[staff.post_name]) groups[staff.post_name] = [];
          groups[staff.post_name].push(staff);
        });
        this.staffRemarksMass = groups;
        if (!rows.length) this.fetchMassError = 'Aucune remarque pour cette période.';
      } catch (err) {
        this.staffRemarksMass = {};
        this.fetchMassError = err.message;
      } finally {
        this.loading = false;
      }
    },
    async fetchPosts() {
      try {
        const res = await fetch('/api-pointage/fetchposts', { credentials: 'include', cache: 'no-store' });
        if (!res.ok) throw new Error('Erreur postes');
        const data = await res.json();
        this.posts = Array.isArray(data) ? data : [];
      } catch {
        this.posts = [];
      }
    },
    async addRemarque() {
      if (!this.selectedStaff || !this.newRemark.trim()) return;
      this.savingRemark = true;
      try {
        const res = await fetch('/api-pointage/addremarque', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            staff_id: this.selectedStaff.Id || this.selectedStaff.id,
            remarque: this.newRemark.trim(),
          }),
        });
        if (!res.ok) throw new Error('Erreur ajout remarque');
        this.newRemark = '';
        await this.fetchRemarques(this.selectedStaff);
      } catch (err) {
        alert(err.message);
      } finally {
        this.savingRemark = false;
      }
    },
    openEditRemarque(record) {
      this.editingRemarque = record;
      this.editText = record.remarque;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingRemarque = null;
      this.editText = '';
    },
    async updateRemarque() {
      if (!this.editingRemarque || !this.editText.trim()) return;
      try {
        const res = await fetch('/api-pointage/updateremarque', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.editingRemarque.id, remarque: this.editText.trim() }),
        });
        if (!res.ok) throw new Error('Erreur modification remarque');
        this.closeEditModal();
        await this.fetchRemarques(this.selectedStaff);
      } catch (err) {
        alert(err.message);
      }
    },
    async deleteRemarque(record) {
      if (!confirm('Supprimer cette remarque ?')) return;
      try {
        const res = await fetch('/api-pointage/deleteremarque', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: record.id }),
        });
        if (!res.ok) throw new Error('Erreur suppression remarque');
        this.records = this.records.filter((item) => item.id !== record.id);
      } catch (err) {
        alert(err.message);
      }
    },
    convertdate(input) {
      if (!input) return '-';
      const normalized = String(input).replace('T', ' ');
      const [date, time = ''] = normalized.split(' ');
      const [year, month, day] = date.includes('-') ? date.split('-') : date.split('/');
      return `${day}/${month}/${year}${time ? ` ${time.slice(0, 5)}` : ''}`;
    },
  },
};
</script>

<style scoped>
.remarks-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.remarks-header,
.control-panel,
.staff-card,
.note-card,
.list-card,
.metric-card,
.post-group {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.remarks-header {
  min-height: 82px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.eyebrow {
  margin: 0 0 4px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

h1, h2, h3, p {
  margin: 0;
}

.remarks-header h1 {
  color: var(--gray-900);
  font-size: 25px;
}

.remarks-header p:not(.eyebrow) {
  margin-top: 4px;
  color: var(--gray-500);
  font-size: 13px;
}

.btn-primary,
.btn-ghost,
.icon-btn {
  min-height: 40px;
  border-radius: 8px;
  font-weight: 800;
  cursor: pointer;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  padding: 0 15px;
  color: #fff;
  background: var(--primary);
}

.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary svg {
  width: 16px;
  height: 16px;
}

.btn-ghost {
  border: 1px solid var(--border);
  background: #f8fafc;
  color: var(--gray-700);
  padding: 0 14px;
}

.control-panel {
  padding: 12px 16px;
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  gap: 14px;
  align-items: start;
  position: relative;
  z-index: 3;
}

.search-block,
.filters-block label {
  display: grid;
  gap: 6px;
  color: var(--gray-600);
  font-size: 12px;
  font-weight: 800;
}

.search-input-wrap {
  position: relative;
}

.search-input-wrap svg {
  position: absolute;
  width: 16px;
  height: 16px;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
}

.search-input-wrap input,
.filters-block input,
.filters-block select,
textarea {
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-900);
  padding: 0 12px;
}

.search-input-wrap input {
  padding-left: 38px;
}

.filters-block {
  display: grid;
  grid-template-columns: 150px 190px;
  gap: 12px;
}

.search-menu {
  position: absolute;
  left: 16px;
  right: 16px;
  top: calc(100% - 10px);
  max-height: 320px;
  overflow: auto;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  box-shadow: var(--shadow-lg);
}

.search-menu button {
  width: 100%;
  min-height: 58px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  text-align: left;
  cursor: pointer;
}

.search-menu button:hover {
  background: #f8fafc;
}

.search-menu img {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.search-menu span {
  display: grid;
  gap: 2px;
}

.search-menu small,
.remark-body span,
.staff-row small,
.metric-card span,
.post-group-head span,
.note-actions span {
  color: var(--gray-500);
  font-size: 12px;
}

.inline-error {
  color: #b45309;
  font-size: 12px;
}

.staff-workspace {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
}

.staff-card {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  text-align: center;
  align-self: start;
}

.back-btn {
  width: 100%;
  margin-bottom: 6px;
}

.staff-card img {
  width: 92px;
  height: 92px;
  border-radius: 14px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.staff-card h2 {
  color: var(--gray-900);
  font-size: 20px;
}

.staff-card span {
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--primary-glow);
  color: var(--primary);
  font-size: 12px;
  font-weight: 800;
}

.staff-card strong,
.count-pill {
  color: var(--gray-900);
}

.remarks-workspace {
  display: grid;
  gap: 16px;
}

.note-card,
.list-card {
  padding: 16px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-head h3 {
  color: var(--gray-900);
  font-size: 17px;
}

.note-card textarea,
.modal-textarea {
  min-height: 118px;
  resize: vertical;
  padding: 12px;
  line-height: 1.5;
}

.note-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.count-pill {
  min-width: 34px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f1f5f9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.remark-list {
  display: grid;
  gap: 8px;
}

.remark-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
}

.remark-body {
  display: grid;
  gap: 7px;
}

.remark-body p {
  color: var(--gray-900);
  line-height: 1.5;
}

.row-actions {
  display: flex;
  gap: 7px;
}

.icon-btn {
  width: 38px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--gray-600);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-btn svg {
  width: 16px;
  height: 16px;
}

.icon-btn.danger {
  color: #dc2626;
  background: #fef2f2;
  border-color: #fecaca;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.metric-card {
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.metric-card strong {
  color: var(--gray-900);
  font-size: 24px;
}

.post-groups {
  display: grid;
  gap: 10px;
}

.post-group {
  overflow: hidden;
}

.post-group-head {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.post-group-head h2 {
  font-size: 16px;
}

.staff-row {
  width: 100%;
  min-height: 52px;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 9px 16px;
  cursor: pointer;
  text-align: left;
}

.staff-row:last-child {
  border-bottom: 0;
}

.staff-row:hover {
  background: #f8fafc;
}

.staff-row span {
  display: grid;
  gap: 3px;
}

.staff-row strong {
  color: var(--gray-900);
}

.staff-row em {
  min-width: 34px;
  height: 30px;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-style: normal;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  min-height: 88px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
  background: #fff;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.modal-content {
  width: min(560px, calc(100vw - 36px));
  background: #fff;
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 16px;
  box-shadow: var(--shadow-lg);
}

.modal-header,
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-header {
  margin-bottom: 12px;
}

.modal-footer {
  margin-top: 12px;
  justify-content: flex-end;
}

@media (max-width: 900px) {
  .remarks-header,
  .control-panel,
  .staff-workspace,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .remarks-header,
  .note-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .filters-block {
    grid-template-columns: 1fr;
  }

  .staff-workspace {
    display: grid;
  }

  .remark-row {
    grid-template-columns: 1fr;
  }

  .row-actions {
    justify-content: flex-end;
  }

  .btn-primary,
  .btn-ghost {
    width: 100%;
  }
}
</style>
